import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, RefObject } from "react";

/**
 * Shared bar-chart hover-tooltip behavior: tracks which bar is hovered,
 * clamps the floating tooltip to the viewport, and flips it below the bar
 * when it would clip the top of the screen. Used by any mini/full bar chart
 * that renders its tooltip in a fixed-position element outside the chart's
 * own overflow.
 */
export function useBarHoverTooltip(containerRef?: RefObject<HTMLElement>) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltipColor, setTooltipColor] = useState<string>("");
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const [tooltipFlipped, setTooltipFlipped] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoveredBarRef = useRef<HTMLElement | null>(null);

  const positionTooltip = useCallback(() => {
    const barEl = hoveredBarRef.current;
    const ttEl = tooltipRef.current;
    if (!barEl || !ttEl) return;

    const bR = barEl.getBoundingClientRect();
    const ttW = ttEl.offsetWidth;
    const ttH = ttEl.offsetHeight;

    const left = bR.left + bR.width / 2 - ttW / 2;
    const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - ttW - 8));

    const topAbove = bR.top - ttH - 10;
    const isFlipped = topAbove < 8;
    const top = isFlipped ? bR.bottom + 8 : topAbove;

    const arrowX = Math.max(14, Math.min(bR.left + bR.width / 2 - clampedLeft, ttW - 14));

    setTooltipFlipped(isFlipped);
    setTooltipStyle({
      left: `${clampedLeft}px`,
      top: `${top}px`,
      "--arrow-x": `${arrowX}px`,
    } as CSSProperties);
  }, []);

  useEffect(() => {
    if (hoveredIdx !== null) positionTooltip();
  }, [hoveredIdx, positionTooltip]);

  useEffect(() => {
    window.addEventListener("resize", positionTooltip);
    return () => window.removeEventListener("resize", positionTooltip);
  }, [positionTooltip]);

  // Reposition when the chart container itself resizes (e.g. sidebar open/close)
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;
    const ro = new ResizeObserver(positionTooltip);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, positionTooltip]);

  const showTooltip = useCallback(
    (el: HTMLElement | null, idx: number, color: string) => {
      hoveredBarRef.current = el;
      setTooltipColor(color);
      setHoveredIdx(idx);
    },
    [],
  );

  const hideTooltip = useCallback(() => {
    hoveredBarRef.current = null;
    setHoveredIdx(null);
  }, []);

  const reset = useCallback(() => {
    hoveredBarRef.current = null;
    setHoveredIdx(null);
    setTooltipFlipped(false);
  }, []);

  return {
    hoveredIdx,
    tooltipColor,
    tooltipStyle,
    tooltipFlipped,
    tooltipRef,
    showTooltip,
    hideTooltip,
    reset,
  };
}
